import React from 'react'
import { X } from 'lucide-react'
import { Input } from '@/components/ui/input'

interface PreferenceMultiSelectProps {
  label: string
  description?: string
  selectedItems: string[]
  onItemsChange: (items: string[]) => void
  placeholder?: string
  suggestions?: string[]
  restrictToSuggestions?: boolean // Only allow items from the suggestions list
  helperText?: string
  saveStatus?: 'idle' | 'saving' | 'saved' | 'error'
  showHelperOnFocusOnly?: boolean
  reserveHelperSpace?: boolean
}

const PreferenceMultiSelect: React.FC<PreferenceMultiSelectProps> = ({
  label,
  description,
  selectedItems,
  onItemsChange,
  placeholder = 'Add item...',
  suggestions = [],
  restrictToSuggestions = false,
  helperText,
  saveStatus = 'idle',
  showHelperOnFocusOnly = false,
  reserveHelperSpace = false,
}) => {
  const [inputValue, setInputValue] = React.useState('')
  const [showSuggestions, setShowSuggestions] = React.useState(false)
  const [showError, setShowError] = React.useState(false)
  const [isInputFocused, setIsInputFocused] = React.useState(false)

  const filteredSuggestions = suggestions.filter(
    (item) =>
      !selectedItems.includes(item) && item.toLowerCase().includes(inputValue.toLowerCase()),
  )

  const handleAddItem = (value: string, source: 'input' | 'suggestion' = 'input') => {
    const trimmedValue = value.trim()
    if (trimmedValue && !selectedItems.includes(trimmedValue)) {
      // If restrictToSuggestions is true, only allow items from the suggestions list
      if (restrictToSuggestions && !suggestions.includes(trimmedValue)) {
        setShowError(true)
        setTimeout(() => setShowError(false), 2000) // Hide error after 2 seconds
        return // Don't add invalid items
      }
      onItemsChange([...selectedItems, trimmedValue])
      setInputValue('')
      setShowSuggestions(source === 'suggestion')
      setShowError(false)
    } else if (selectedItems.includes(trimmedValue)) {
      // Item already selected
      setInputValue('')
      setShowSuggestions(source === 'suggestion')
    }
  }

  const handleRemoveItem = (itemToRemove: string) => {
    onItemsChange(selectedItems.filter((item) => item !== itemToRemove))
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddItem(inputValue)
    } else if (e.key === 'Escape') {
      setShowSuggestions(false)
    }
  }

  return (
    <div className="space-y-2">
      <div>
        <label className="text-sm font-medium text-foreground">{label}</label>
        {description && <p className="text-sm text-gray-600">{description}</p>}
      </div>

      <div className="flex flex-wrap gap-2 mb-2">
        {selectedItems.map((item) => (
          <div
            key={item}
            className="flex items-center bg-[#C0FD72]/20 text-[#4d7c0f] border border-[#C0FD72]/30 rounded-full px-3 py-1 text-sm"
          >
            <span>{item}</span>
            <button
              type="button"
              onClick={() => handleRemoveItem(item)}
              className="ml-1 p-0.5 rounded-full hover:bg-[#C0FD72]/30 focus:outline-hidden"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>

      <div className="space-y-3">
        <Input
          type="text"
          placeholder={placeholder}
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value)
            setShowSuggestions(true)
            setShowError(false) // Clear error when user starts typing
          }}
          onFocus={() => {
            setShowSuggestions(true)
            setIsInputFocused(true)
          }}
          onBlur={() => {
            setIsInputFocused(false)
            // Delay hiding suggestions to allow for clicks
            setTimeout(() => setShowSuggestions(false), 150)
          }}
          onKeyDown={handleKeyDown}
          className="bg-card border-foreground/10 text-foreground placeholder:text-gray-600"
        />

        {(helperText || saveStatus !== 'idle' || reserveHelperSpace) && (
          <div className="flex items-center justify-between gap-3 min-h-4">
            <p
              className={`text-xs text-gray-600 transition-opacity ${
                helperText && (!showHelperOnFocusOnly || isInputFocused)
                  ? 'opacity-100'
                  : 'opacity-0'
              }`}
            >
              {helperText || '\u00A0'}
            </p>
            {saveStatus === 'saving' && <span className="text-xs text-gray-600">Saving...</span>}
            {saveStatus === 'saved' && <span className="text-xs text-[#4d7c0f]">Saved</span>}
            {saveStatus === 'error' && <span className="text-xs text-red-600">Save failed</span>}
          </div>
        )}

        {showError && restrictToSuggestions && (
          <p className="text-sm text-red-600">Please select from the available options.</p>
        )}

        {showSuggestions && filteredSuggestions.length > 0 && (
          <div>
            <div className="flex flex-wrap gap-2">
              {filteredSuggestions.slice(0, 8).map((suggestion) => (
                <button
                  key={suggestion}
                  className="px-3 py-1 text-sm bg-card hover:bg-foreground/10 text-gray-700 hover:text-foreground border border-foreground/10 hover:border-foreground/20 rounded-full transition-colors focus:outline-hidden focus:ring-2 focus:ring-[#C0FD72]/50"
                  onMouseDown={(e) => {
                    e.preventDefault() // Prevent blur before click
                    handleAddItem(suggestion, 'suggestion')
                  }}
                >
                  {suggestion}
                </button>
              ))}
              {filteredSuggestions.length > 8 && (
                <span className="px-3 py-1 text-xs text-gray-500 self-center">
                  Type to search for more
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default PreferenceMultiSelect
